/**
 * Contracts Module - TanStack Query Hooks
 *
 * This module provides all contract-related hooks for:
 * - Fetching contracts list with pagination
 * - Getting single contract details
 * - Creating contracts
 * - Updating contracts
 * - Confirming contracts
 * - Previewing contracts (PDF)
 * - Downloading contracts (Word)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PaginationTable } from "js/components/types";

import {
  confirmContract,
  createContract,
  downloadWordDocumentContract,
  getContractDetails,
  previewContract,
  retrieveContract,
  retrieveContractsList,
  updateContract,
} from "../api/contracts";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS - GET Operations
// ============================================================================

/**
 * Hook to get contracts list with pagination and optional filters
 */
export const useGetContracts = (
  pagination: PaginationTable,
  searchParams?: { search?: string; status?: string; is_active?: string },
) => {
  return useQuery({
    queryKey: queryKeys.contracts.list({ pagination, searchParams }),
    queryFn: () => retrieveContractsList(pagination, searchParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get a single contract by ID
 */
export const useGetContract = (contractId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.contracts.detail(contractId || ""),
    queryFn: () => retrieveContract(contractId),
    enabled: !!contractId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get contract details (full contract data including all related entities)
 */
export const useGetContractDetails = (contractId: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.contracts.detail(contractId || ""), "details"],
    queryFn: () => getContractDetails(contractId || ""),
    enabled: !!contractId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to preview contract (returns PDF blob)
 * This is a query because it's fetching data for preview purposes
 */
export const usePreviewContract = (contractId: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.contracts.detail(contractId || ""), "preview"],
    queryFn: () => previewContract(contractId || ""),
    enabled: false, // Manual trigger only
    staleTime: 0, // Always fetch fresh preview
  });
};

// ============================================================================
// MUTATION HOOKS - CREATE, UPDATE, DELETE Operations
// ============================================================================

/**
 * Hook to create a new contract
 *
 * Invalidates:
 * - contracts list (new contract appears in list)
 * - tenant reports (may be created from a report)
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contractData: any) => createContract(contractData),
    onSuccess: () => {
      toast.success("Contrato creado exitosamente");

      // Invalidate contracts lists
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });

      // Invalidate tenant reports (contract may be linked to a report)
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantReports.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al crear el contrato";
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to update an existing contract
 *
 * Invalidates:
 * - contracts list
 * - specific contract detail
 */
export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      contractData,
    }: {
      contractId: string;
      contractData: any;
    }) => updateContract(contractId, contractData),
    onSuccess: (_, variables) => {
      toast.success("Contrato actualizado exitosamente");

      // Invalidate contracts lists
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });

      // Invalidate specific contract
      queryClient.invalidateQueries({
        queryKey: queryKeys.contracts.detail(variables.contractId),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar el contrato";
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to confirm a contract
 *
 * Invalidates:
 * - contracts list
 * - specific contract detail
 */
export const useConfirmContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contractId: string) => confirmContract(contractId),
    onSuccess: (_, contractId) => {
      toast.success("Contrato confirmado exitosamente");

      // Invalidate contracts lists
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });

      // Invalidate specific contract
      queryClient.invalidateQueries({
        queryKey: queryKeys.contracts.detail(contractId),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al confirmar el contrato";
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to download contract as Word document
 *
 * This is a mutation because it may trigger document generation
 * and we want to track loading/error states
 */
export const useDownloadContractWord = () => {
  return useMutation({
    mutationFn: ({
      contractId,
      downloadAlternativeJustice = false,
      downloadSecureRentPolicy = false,
    }: {
      contractId: string;
      downloadAlternativeJustice?: boolean;
      downloadSecureRentPolicy?: boolean;
    }) =>
      downloadWordDocumentContract(
        contractId,
        downloadAlternativeJustice,
        downloadSecureRentPolicy,
      ),
    onSuccess: (response, variables) => {
      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Backend exposes the human-readable filename (folio or document_id)
      // via the `x-filename` header. Fall back to UUID only if missing.
      const serverFilename = response.headers?.["x-filename"];
      link.download = serverFilename
        ? `${serverFilename.replace(/\.[^.]+$/, "")}.docx`
        : `contrato-${variables.contractId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Contrato descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al descargar el contrato";
      toast.error(errorMessage);
    },
  });
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export all hooks as named exports
export default {
  useGetContracts,
  useGetContract,
  useGetContractDetails,
  usePreviewContract,
  useCreateContract,
  useUpdateContract,
  useConfirmContract,
  useDownloadContractWord,
};
